import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, Fragment as _Fragment, renderList as _renderList, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  class: "ifc-importer-page",
  style: {"box-sizing":"border-box","display":"flex","flex-direction":"column"}
}
const _hoisted_2 = { style: {"display":"flex","align-items":"center","gap":"15px","margin-bottom":"15px","flex-shrink":"0","flex-wrap":"nowrap"} }
const _hoisted_3 = {
  key: 0,
  class: "ifc-progress"
}
const _hoisted_4 = { class: "ifc-progress__bar" }
const _hoisted_5 = { class: "ifc-progress__pct" }
const _hoisted_6 = ["title"]
const _hoisted_7 = {
  key: 1,
  class: "ifc-progress"
}
const _hoisted_8 = { class: "ifc-progress__bar" }
const _hoisted_9 = { class: "ifc-progress__pct" }
const _hoisted_10 = { class: "ifc-progress__count" }
const _hoisted_11 = ["title"]
const _hoisted_12 = {
  key: 1,
  class: "ifc-progress__tag ifc-progress__tag--ok"
}
const _hoisted_13 = {
  key: 2,
  class: "ifc-progress__tag ifc-progress__tag--skip"
}
const _hoisted_14 = {
  key: 3,
  class: "ifc-progress__tag ifc-progress__tag--fail"
}
const _hoisted_15 = {
  key: 2,
  style: {"color":"#c0392b"}
}
const _hoisted_16 = { style: {"display":"flex","gap":"8px","margin-bottom":"10px","align-items":"center","flex-shrink":"0","flex-wrap":"wrap"} }
const _hoisted_17 = ["disabled"]
const _hoisted_18 = ["disabled"]
const _hoisted_19 = ["disabled"]
const _hoisted_20 = {
  key: 0,
  class: "ifc-whroute-badge"
}
const _hoisted_21 = {
  key: 0,
  style: {"position":"absolute","inset":"0","background":"rgba(30,40,50,0.7)","display":"flex","align-items":"center","justify-content":"center","z-index":"10","color":"white","font-size":"24px"}
}
const _hoisted_22 = { style: {"font-weight":"bold"} }
const _hoisted_23 = { style: {"color":"#aaa"} }

import { ref, reactive, computed, watch, onUnmounted, onMounted, nextTick, createApp, type App } from 'vue';
import { HubConnection, HubConnectionBuilder, HttpTransportType } from '@microsoft/signalr';
import { CookieManager } from 'Core/Common/CookieManager';
import { Modal } from 'Core/Common/Modal';
import { IfcApi, IfcNodeDto, IfcImportResultDto } from 'Core/Components/Ifc/IfcApi';
import { IfcViewerCore, ViewPreset, ViewerTool } from 'Core/Components/Ifc/IfcViewerCore';
import IfcTreeNode from 'Core/Components/Ifc/IfcTreeNode.vue';
import IfcPropertiesPanel from 'Core/Components/Ifc/IfcPropertiesPanel.vue';
import IfcWorkbench from 'Core/Components/Ifc/IfcWorkbench.vue';
import IfcViewerToolbar from 'Core/Components/Ifc/IfcViewerToolbar.vue';
import IfcClassVisibilityBar from 'Core/Components/Ifc/IfcClassVisibilityBar.vue';
import BsddInfoModal from 'Core/Components/Ifc/BsddInfoModal.vue';
import WarehouseRoutingModal from './WarehouseRoutingModal.vue';
import IfcIcon from 'Core/Components/Ifc/IfcIcon.vue';

interface ImportProgress { done: number; total: number; current: string; status: string; }

// State
interface WarehousePick { id: number; name: string; }

export default /*@__PURE__*/_defineComponent({
  __name: 'IfcImporterPage',
  setup(__props) {

const tree = ref<IfcNodeDto[]>([]);
const fileName = ref('');
const totalElements = ref(0);
const isLoading = ref(false);
const loadingMessage = ref('');
const loadingPercent = ref(0);
const nodeCount = ref(0);
const isImporting = ref(false);
const importError = ref('');
const selectedNode = ref<IfcNodeDto | null>(null);
const expandedIds = reactive<Record<number, boolean>>({});
const importStatuses = ref<Map<number, 'Pending' | 'Created' | 'Skipped' | 'Failed'>>(new Map());
const existingRecords = ref<Record<string, number>>({});
const existingEntityId = ref<number>(0);

// 2D drawing
const drawingSvg = ref<string>('');
const drawingLoading = ref(false);
const drawingError = ref<string>('');
// Non-fatal: set when the 3D preview can't load (e.g. wasm not served on this
// deployment); the structure tree and import stay fully usable.
const previewError = ref<string>('');
const drawingGlobalId = ref<string>('');

// Warehouse routing state (per-import, not persisted)
const defaultWarehouse = ref<WarehousePick | null>(null);
const classWarehouseMappings = reactive<Record<string, WarehousePick>>({});
const routedClassesCount = computed(() => Object.keys(classWarehouseMappings).length);

// Reposition a jBox modal whenever its mounted content resizes
// (Vue re-renders, async data loads, list expansions, etc.).
const wireModalAutoReposition = (modal: Modal, mountPoint: HTMLElement): ResizeObserver => {
	let raf: number | null = null;
	const observer = new ResizeObserver(() => {
		if (raf !== null) return;
		raf = requestAnimationFrame(() => {
			raf = null;
			try { modal.Reposition(); } catch {}
		});
	});
	observer.observe(mountPoint);
	return observer;
};

// Warehouse routing modal
let whModalInstance: Modal | null = null;
let whVueApp: App | null = null;
let whResizeObserver: ResizeObserver | null = null;

const openWarehouseModal = () => {
	whVueApp?.unmount();
	whVueApp = null;
	whResizeObserver?.disconnect();
	whResizeObserver = null;
	whModalInstance?.Destroy();

	const modal = new Modal({
		minWidth: '560px',
		maxWidth: '760px',
		minHeight: '400px',
		closeButton: 'box',
		closeOnEsc: true,
	}, false);

	const mountPoint = document.createElement('div');
	modal.SetContent($(mountPoint));

	whVueApp = createApp(WarehouseRoutingModal, {
		ifcClasses: allClasses.value,
		initialDefault: defaultWarehouse.value,
		initialMappings: { ...classWarehouseMappings },
		onApply: (payload: { defaultWarehouse: WarehousePick | null; mappings: Record<string, WarehousePick> }) => {
			defaultWarehouse.value = payload.defaultWarehouse;
			// Replace (not merge) so removed mappings are forgotten
			Object.keys(classWarehouseMappings).forEach(k => delete classWarehouseMappings[k]);
			Object.assign(classWarehouseMappings, payload.mappings);
			modal.Close();
		},
		onClose: () => modal.Close(),
	});
	whVueApp.mount(mountPoint);

	modal.On('CLOSE', null, () => {
		whVueApp?.unmount();
		whVueApp = null;
		whResizeObserver?.disconnect();
		whResizeObserver = null;
		whModalInstance = null;
	});

	whModalInstance = modal;
	modal.Show();
	whResizeObserver = wireModalAutoReposition(modal, mountPoint);
};

// bSDD modal — renders the Vue component inside the project's jBox-based Modal
let bsddModalInstance: Modal | null = null;
let bsddVueApp: App | null = null;
let bsddResizeObserver: ResizeObserver | null = null;

const openBsddModal = () => {
	if (!selectedNode.value) return;

	// Dispose any previous instance so clicking different nodes replaces content
	bsddVueApp?.unmount();
	bsddVueApp = null;
	bsddResizeObserver?.disconnect();
	bsddResizeObserver = null;
	bsddModalInstance?.Destroy();

	const modal = new Modal({
		minWidth: '600px',
		maxWidth: '960px',
		minHeight: '400px',
		closeButton: 'box',
		closeOnEsc: true,
	}, false);

	const mountPoint = document.createElement('div');
	modal.SetContent($(mountPoint));

	bsddVueApp = createApp(BsddInfoModal, { node: selectedNode.value });
	bsddVueApp.mount(mountPoint);

	modal.On('CLOSE', null, () => {
		bsddVueApp?.unmount();
		bsddVueApp = null;
		bsddResizeObserver?.disconnect();
		bsddResizeObserver = null;
		bsddModalInstance = null;
	});

	bsddModalInstance = modal;
	modal.Show();
	bsddResizeObserver = wireModalAutoReposition(modal, mountPoint);
};

// Import progress (streamed via SignalR)
const importDone = ref(0);
const importTotal = ref(0);
const importCurrent = ref('');
const importCreated = ref(0);
const importSkippedCount = ref(0);
const importFailed = ref(0);
let importHub: HubConnection | null = null;

const importPercent = computed(() => importTotal.value > 0 ? Math.round((importDone.value / importTotal.value) * 100) : 0);

const hasData = computed(() => tree.value.length > 0);

// Preview renderer state
const preview = new IfcViewerCore();
const workbench = ref<InstanceType<typeof IfcWorkbench> | null>(null);
const getViewportEl = (): HTMLDivElement | null => {
	return (workbench.value?.viewportEl as HTMLDivElement | null) ?? null;
};
let previewMounted = false;
const is3dLoading = ref(false);
const ghostMode = ref(false);
const activeTool = ref<ViewerTool>('none');
const hoveredNode = ref<IfcNodeDto | null>(null);
const hoverPos = ref({ x: 0, y: 0 });
const hiddenClasses = reactive<Record<string, boolean>>({});
const treeCollapsed = ref(false);
const propsCollapsed = ref(false);

// Classes skipped during import (IfcProject/IfcSite exist only as spatial-structure
// anchors and aren't written to ARTICLES). Hide them from the visibility icon bar
// and the warehouse routing modal so the UI only offers classes that actually import.
const SKIPPED_IFC_CLASSES = new Set(['IfcProject', 'IfcSite']);

// All distinct IFC classes in the tree, for visibility toggle panel
const allClasses = computed(() => {
	const set = new Set<string>();
	const walk = (nodes: IfcNodeDto[]) => {
		for (const n of nodes) {
			if (n.ifcClass && !SKIPPED_IFC_CLASSES.has(n.ifcClass)) set.add(n.ifcClass);
			walk(n.children);
		}
	};
	walk(tree.value);
	return Array.from(set).sort();
});

const keyHandler = (e: KeyboardEvent) => {
	if (!hasData.value) return;
	const target = e.target as HTMLElement;
	if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;
	if (e.key === 'f' || e.key === 'F') { e.preventDefault(); preview.fitToSelection(); }
	else if (e.key === 'a' || e.key === 'A') { e.preventDefault(); preview.showAll(); }
	else if (e.key === 'Escape') { e.preventDefault(); selectedNode.value = null; }
};

onMounted(async () => {
	window.addEventListener('keydown', keyHandler);

	// Connect to the SignalR hub to receive import progress events
	try {
		importHub = new HubConnectionBuilder()
			.withUrl('/api/signalRHub', {
				accessTokenFactory: () => CookieManager.GetAuthToken() ?? '',
				skipNegotiation: true,
				transport: HttpTransportType.WebSockets
			})
			.withAutomaticReconnect()
			.build();

		importHub.on('IfcImportProgress', (p: ImportProgress) => {
			importDone.value = p.done;
			importTotal.value = p.total;
			importCurrent.value = p.current || '';
			if (p.status === 'created') importCreated.value++;
			else if (p.status === 'skipped') importSkippedCount.value++;
			else if (p.status === 'failed') importFailed.value++;
		});

		await importHub.start();
	} catch (err) {
		console.warn('[IfcImporter] SignalR hub connection failed:', err);
	}
});
onUnmounted(() => {
	window.removeEventListener('keydown', keyHandler);
	preview.dispose();
	importHub?.stop().catch(() => {});
	bsddVueApp?.unmount();
	bsddResizeObserver?.disconnect();
	bsddModalInstance?.Destroy();
	whVueApp?.unmount();
	whResizeObserver?.disconnect();
	whModalInstance?.Destroy();
	// Release server-side IFC file + cached SVG
	if (fileName.value) {
		try { IfcApi.Cleanup(fileName.value); } catch {}
	}
});

// File selection
const fileInput = ref<HTMLInputElement | null>(null);

const selectFile = () => {
	fileInput.value?.click();
};

const onFileSelected = async (event: Event) => {
	const input = event.target as HTMLInputElement;
	if (!input.files || input.files.length === 0) return;

	const file = input.files[0];

	// Clean up previous file's server-side artifacts (IFC + cached SVG)
	if (fileName.value && fileName.value !== file.name) {
		try { await new Promise<void>((r, j) => IfcApi.Cleanup(fileName.value).then(() => r()).fail(j)); } catch {}
	}

	fileName.value = file.name;
	isLoading.value = true;
	// Show the 3D overlay immediately so the old model is hidden as soon as
	// the new tree streams in (before loadFile even starts).
	is3dLoading.value = true;
	loadingMessage.value = 'Uploading...';
	loadingPercent.value = 0;
	tree.value = [];
	nodeCount.value = 0;
	selectedNode.value = null;
	drawingSvg.value = '';
	drawingError.value = '';
	previewError.value = '';
	Object.keys(expandedIds).forEach(k => delete expandedIds[k]);
	importStatuses.value.clear();
	defaultWarehouse.value = null;
	Object.keys(classWarehouseMappings).forEach(k => delete classWarehouseMappings[k]);

	try {
		// Upload chunks
		await IfcApi.Upload(file, (percent, message) => {
			loadingPercent.value = percent;
			loadingMessage.value = message;
		});

		// Stream parse results via SSE
		loadingMessage.value = 'Parsing IFC...';
		loadingPercent.value = 85;

		let parseError = '';
		await IfcApi.ParseStream(file.name, {
			onNode: () => {
				nodeCount.value++;
				loadingMessage.value = `Parsing... ${nodeCount.value} elements`;
			},
			onComplete: (result) => {
				tree.value = result.tree;
				totalElements.value = result.totalElements;

				// Auto-expand first two levels
				const expand = (nodes: IfcNodeDto[], depth: number) => {
					for (const n of nodes) {
						if (depth < 2) {
							expandedIds[n.expressId] = true;
							expand(n.children, depth + 1);
						}
					}
				};
				expand(tree.value, 0);
			},
			onError: (msg) => { parseError = msg; }
		});

		if (parseError) throw new Error(parseError);

		// Lookup which GlobalIds already exist as imported ARTICLES
		try {
			const allIds: string[] = [];
			for (const root of tree.value) allIds.push(...collectGlobalIds(root));
			if (allIds.length > 0) {
				const existing = await new Promise<any>((resolve, reject) => {
					IfcApi.FindExisting(allIds).then(resolve).fail(reject);
				});
				existingEntityId.value = existing.entityId || 0;
				existingRecords.value = existing.records || {};
			}
		} catch (_) { /* non-fatal */ }

		// Seed the per-class routing from the persisted automapping (WAREHOUSES /
		// IFCCLASSES sub-table): the import uses it even when the routing modal is
		// never opened, and the modal shows the same picks preselected. Manual
		// picks (none yet on a fresh parse) always win over the automapping.
		try {
			const [warehouses, mapResp] = await Promise.all([
				new Promise<Array<{ id: number; name: string }>>((resolve, reject) => {
					IfcApi.GetWarehouses().then(resolve).fail(reject);
				}),
				new Promise<{ mappings: Record<string, number> }>((resolve, reject) => {
					IfcApi.GetWarehouseClassMappings().then(resolve).fail(reject);
				}),
			]);
			const nameById = new Map<number, string>(warehouses.map(w => [w.id, w.name]));
			const auto = new Map<string, number>(
				Object.entries(mapResp?.mappings ?? {}).map(([cls, id]) => [cls.toLowerCase(), Number(id)]),
			);
			for (const cls of allClasses.value) {
				if (classWarehouseMappings[cls]) continue;
				const warehouseId = auto.get(cls.toLowerCase());
				if (warehouseId && warehouseId > 0) {
					classWarehouseMappings[cls] = { id: warehouseId, name: nameById.get(warehouseId) ?? `#${warehouseId}` };
				}
			}
		} catch (_) { /* automapping unavailable — manual routing only */ }

		// Load 3D preview. The tree is already populated and usable, so a
		// preview failure (e.g. the wasm not served on a given deployment) must
		// NOT blank the page or hang the loader — it degrades to "tree only".
		loadingMessage.value = 'Loading 3D preview...';
		loadingPercent.value = 95;
		try {
			// Mount renderer on first file (viewport DOM now exists because hasData is true)
			await nextTick();
			const host = getViewportEl();
			if (!previewMounted && host) {
				await preview.mount(host);
				previewMounted = true;
			}
			// Real conversion progress: big models spend MINUTES in the client-side
			// IFC→fragments conversion — map its 0..1 onto the loader's 95..100 so
			// the bar visibly moves instead of freezing at "95%".
			await preview.loadFile(file, (fraction) => {
				loadingPercent.value = Math.min(99, 95 + Math.round(fraction * 5));
				loadingMessage.value = fraction >= 1
					? 'Finalizing 3D preview...'
					: `Loading 3D preview... ${Math.round(fraction * 100)}%`;
			});
		} catch (previewErr: any) {
			// eslint-disable-next-line no-console
			console.error('[IFC] 3D preview failed (tree/import still available):', previewErr);
			previewError.value = previewErr?.message || '3D preview unavailable';
		}
		loadingMessage.value = '';
	} catch (err: any) {
		loadingMessage.value = `Error: ${err.message || 'Parse failed'}`;
	} finally {
		isLoading.value = false;
		is3dLoading.value = false;
		input.value = '';
	}
};

// Tree expand/collapse
const toggleExpand = (node: IfcNodeDto) => {
	if (expandedIds[node.expressId]) {
		delete expandedIds[node.expressId];
	} else {
		expandedIds[node.expressId] = true;
	}
};

// Selection
const selectNode = (node: IfcNodeDto) => {
	selectedNode.value = node;
};

const collectGlobalIds = (node: IfcNodeDto): string[] => {
	const ids: string[] = [];
	if (node.globalId) ids.push(node.globalId);
	for (const child of node.children) {
		ids.push(...collectGlobalIds(child));
	}
	return ids;
};

const findNodeByGlobalId = (nodes: IfcNodeDto[], globalId: string): IfcNodeDto | null => {
	for (const node of nodes) {
		if (node.globalId === globalId) return node;
		const found = findNodeByGlobalId(node.children, globalId);
		if (found) return found;
	}
	return null;
};

const expandToNode = (nodes: IfcNodeDto[], target: IfcNodeDto): boolean => {
	for (const node of nodes) {
		if (node === target) return true;
		if (expandToNode(node.children, target)) {
			expandedIds[node.expressId] = true;
			return true;
		}
	}
	return false;
};

let suppressWatch = false;

preview.onElementHovered = (globalId) => {
	if (!globalId) { hoveredNode.value = null; return; }
	hoveredNode.value = findNodeByGlobalId(tree.value, globalId);
};

const onViewportMouseMove = (e: MouseEvent) => {
	const rect = getViewportEl()?.getBoundingClientRect();
	if (!rect) return;
	hoverPos.value = { x: e.clientX - rect.left, y: e.clientY - rect.top };
};

preview.onElementSelected = (globalId) => {
	if (!globalId) {
		if (selectedNode.value) {
			suppressWatch = true;
			selectedNode.value = null;
			nextTick(() => { suppressWatch = false; });
		}
		return;
	}
	const node = findNodeByGlobalId(tree.value, globalId);
	if (node && node !== selectedNode.value) {
		suppressWatch = true;
		expandToNode(tree.value, node);
		selectedNode.value = node;
		nextTick(() => {
			suppressWatch = false;
			// Scroll tree item into view
			const el = document.querySelector(`[data-express-id="${node.expressId}"]`);
			el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
		});
	}
};

watch(selectedNode, async (node) => {
	if (suppressWatch) return;
	if (!node) {
		await preview.showAll();
		return;
	}
	const ids = collectGlobalIds(node);
	if (ids.length === 0) {
		await preview.showAll();
		return;
	}
	await preview.isolate(ids, node.globalId);
});

// Fetch 2D drawing on node selection (only when user actually clicks a node)
const fetchDrawing = async (node: IfcNodeDto | null) => {
	if (!node || !fileName.value) {
		drawingSvg.value = '';
		drawingError.value = '';
		drawingGlobalId.value = '';
		return;
	}
	drawingLoading.value = true;
	drawingError.value = '';
	drawingGlobalId.value = node.globalId || '';
	try {
		const res = await new Promise<{ svg: string; globalId: string | null }>((resolve, reject) => {
			IfcApi.GenerateSvg(fileName.value, node.globalId).then(resolve).fail(reject);
		});
		// Ignore stale responses if the user clicked a different node meanwhile
		if (drawingGlobalId.value === (node.globalId || '')) {
			drawingSvg.value = res.svg || '';
		}
	} catch (err: any) {
		drawingError.value = err?.message || err?.responseJSON?.Message || 'Failed to generate 2D drawing.';
		drawingSvg.value = '';
	} finally {
		drawingLoading.value = false;
	}
};

// 2D drawing generation disabled for now (IfcConvert is too slow on large models
// and 504s at the reverse proxy). Re-enable by un-commenting this watcher and
// un-hiding the `.ifc-props__drawing` block in the template.
// watch(selectedNode, fetchDrawing);
void fetchDrawing;

const showAll = () => preview.showAll();
const fitToSelection = () => preview.fitToSelection();
const setView = (preset: ViewPreset) => preview.setView(preset);
const resetRotation = () => preview.resetRotation();

const toggleGhostMode = () => {
	ghostMode.value = !ghostMode.value;
	preview.setGhostMode(ghostMode.value);
	// Re-apply current selection so hider state updates
	if (selectedNode.value) {
		const ids = collectGlobalIds(selectedNode.value);
		if (ids.length > 0) preview.isolate(ids, selectedNode.value.globalId);
	}
};

const invertIsolation = async () => {
	if (!selectedNode.value) return;
	const ids = collectGlobalIds(selectedNode.value);
	if (ids.length === 0) return;
	await preview.isolate(ids, selectedNode.value.globalId, true);
};

const setTool = (tool: ViewerTool) => {
	activeTool.value = activeTool.value === tool ? 'none' : tool;
	preview.setTool(activeTool.value);
};

const clearMeasurements = () => preview.clearMeasurements();

const toggleClassVisibility = async (cls: string) => {
	hiddenClasses[cls] = !hiddenClasses[cls];
	await preview.hideByClass([cls], hiddenClasses[cls]);
};


const openExistingRecord = (node: IfcNodeDto) => {
	if (!node.globalId || !existingEntityId.value) return;
	const recordId = existingRecords.value[node.globalId];
	if (!recordId) return;
	(window as any).PubSub?.publish('GO_TO_RECORD_SCREEN', {
		EntityId: existingEntityId.value,
		RecordId: recordId,
		IsOpenInModal: true
	});
};

// Import
const importAll = async () => {
	if (tree.value.length === 0) return;

	isImporting.value = true;
	importError.value = '';
	importDone.value = 0;
	importTotal.value = 0;
	importCurrent.value = '';
	importCreated.value = 0;
	importSkippedCount.value = 0;
	importFailed.value = 0;

	try {
		const result: IfcImportResultDto = await new Promise((resolve, reject) => {
			IfcApi.Import(
				tree.value,
				'ARTICLES',
				defaultWarehouse.value?.id ?? null,
				Object.fromEntries(Object.entries(classWarehouseMappings).map(([k, v]) => [k, v.id])),
				fileName.value || null
			)
				.then(resolve)
				.fail(reject);
		});

		const byExpressId = new Map<number, IfcNodeDto>();
		const walk = (nodes: IfcNodeDto[]) => {
			for (const n of nodes) { byExpressId.set(n.expressId, n); walk(n.children); }
		};
		walk(tree.value);

		const updatedRecords: Record<string, number> = { ...existingRecords.value };
		for (const item of result.items) {
			importStatuses.value.set(item.expressId, item.status as 'Created' | 'Skipped' | 'Failed');
			if (!item.recordId) continue;
			const node = byExpressId.get(item.expressId);
			if (node?.globalId) updatedRecords[node.globalId] = item.recordId;
		}
		existingRecords.value = updatedRecords;
	} catch (err: any) {
		importError.value = `Error: ${err.message || 'Import failed'}`;
	} finally {
		isImporting.value = false;
	}
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createCommentVNode(" Header "),
    _createElementVNode("div", _hoisted_2, [
      _cache[2] || (_cache[2] = _createElementVNode("h3", { style: {"margin":"0","flex-shrink":"0"} }, "IFC Importer", -1 /* CACHED */)),
      _createElementVNode("input", {
        ref_key: "fileInput",
        ref: fileInput,
        type: "file",
        accept: ".ifc",
        style: {"display":"none"},
        onChange: onFileSelected
      }, null, 544 /* NEED_HYDRATION, NEED_PATCH */),
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _cache[0] || (_cache[0] = _createElementVNode("i", { class: "fa fa-spinner fa-spin" }, null, -1 /* CACHED */)),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", {
                class: "ifc-progress__fill",
                style: _normalizeStyle({ width: loadingPercent.value + '%' })
              }, null, 4 /* STYLE */)
            ]),
            _createElementVNode("span", _hoisted_5, _toDisplayString(loadingPercent.value) + "%", 1 /* TEXT */),
            (loadingMessage.value)
              ? (_openBlock(), _createElementBlock("span", {
                  key: 0,
                  class: "ifc-progress__current",
                  title: loadingMessage.value
                }, _toDisplayString(loadingMessage.value), 9 /* TEXT, PROPS */, _hoisted_6))
              : _createCommentVNode("v-if", true)
          ]))
        : _createCommentVNode("v-if", true),
      (isImporting.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _cache[1] || (_cache[1] = _createElementVNode("i", { class: "fa fa-spinner fa-spin" }, null, -1 /* CACHED */)),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("div", {
                class: "ifc-progress__fill",
                style: _normalizeStyle({ width: importPercent.value + '%' })
              }, null, 4 /* STYLE */)
            ]),
            _createElementVNode("span", _hoisted_9, _toDisplayString(importPercent.value) + "%", 1 /* TEXT */),
            _createElementVNode("span", _hoisted_10, _toDisplayString(importDone.value) + " / " + _toDisplayString(importTotal.value || '?'), 1 /* TEXT */),
            (importCurrent.value)
              ? (_openBlock(), _createElementBlock("span", {
                  key: 0,
                  class: "ifc-progress__current",
                  title: importCurrent.value
                }, _toDisplayString(importCurrent.value), 9 /* TEXT, PROPS */, _hoisted_11))
              : _createCommentVNode("v-if", true),
            (importCreated.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_12, "+" + _toDisplayString(importCreated.value), 1 /* TEXT */))
              : _createCommentVNode("v-if", true),
            (importSkippedCount.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_13, "↻ " + _toDisplayString(importSkippedCount.value), 1 /* TEXT */))
              : _createCommentVNode("v-if", true),
            (importFailed.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_14, "✕ " + _toDisplayString(importFailed.value), 1 /* TEXT */))
              : _createCommentVNode("v-if", true)
          ]))
        : (importError.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(importError.value), 1 /* TEXT */))
          : _createCommentVNode("v-if", true)
    ]),
    _createCommentVNode(" Actions bar "),
    _createElementVNode("div", _hoisted_16, [
      _createElementVNode("button", {
        class: "btn btn-primary btn-sm ifc-toolbar-btn",
        disabled: isLoading.value,
        title: "Select IFC File",
        onClick: selectFile
      }, [...(_cache[3] || (_cache[3] = [
        _createElementVNode("i", { class: "fa fa-upload" }, null, -1 /* CACHED */)
      ]))], 8 /* PROPS */, _hoisted_17),
      (hasData.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("button", {
              class: "btn btn-success btn-sm ifc-toolbar-btn",
              disabled: isImporting.value,
              title: "Import All to Articles",
              onClick: importAll
            }, [...(_cache[4] || (_cache[4] = [
              _createElementVNode("i", { class: "fa fa-database" }, null, -1 /* CACHED */)
            ]))], 8 /* PROPS */, _hoisted_18),
            _createElementVNode("button", {
              class: "btn btn-default btn-sm ifc-toolbar-btn",
              disabled: isImporting.value,
              title: "Warehouse routing",
              onClick: openWarehouseModal
            }, [
              _cache[5] || (_cache[5] = _createElementVNode("i", { class: "fa fa-truck" }, null, -1 /* CACHED */)),
              (routedClassesCount.value > 0 || defaultWarehouse.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_20, _toDisplayString(routedClassesCount.value > 0 ? routedClassesCount.value : '·'), 1 /* TEXT */))
                : _createCommentVNode("v-if", true)
            ], 8 /* PROPS */, _hoisted_19),
            _createCommentVNode(" Shared viewer toolbar — same buttons in editor + importer. "),
            _cache[6] || (_cache[6] = _createElementVNode("span", { class: "ifc-toolbar-sep-bar" }, null, -1 /* CACHED */)),
            _createVNode(IfcViewerToolbar, {
              "model-loaded": hasData.value,
              "has-selection": !!selectedNode.value,
              "ghost-mode": ghostMode.value,
              "active-tool": activeTool.value,
              onShowAll: showAll,
              onFit: fitToSelection,
              onSetView: setView,
              onResetRotation: resetRotation,
              onToggleGhost: toggleGhostMode,
              onInvertIsolation: invertIsolation,
              onSetTool: setTool,
              onClearMeasurements: clearMeasurements
            }, null, 8 /* PROPS */, ["model-loaded", "has-selection", "ghost-mode", "active-tool"])
          ], 64 /* STABLE_FRAGMENT */))
        : _createCommentVNode("v-if", true)
    ]),
    _createCommentVNode(" Main content: shared workbench shell (toolbar slot above is page-\r\n\t\t     specific; tree/viewport/properties are the same docks the editor\r\n\t\t     uses). v-show keeps the viewport DOM attached across file swaps. "),
    _withDirectives(_createElementVNode("div", {
      style: {"height":"800px"},
      onMousemove: onViewportMouseMove
    }, [
      _createVNode(IfcWorkbench, {
        ref_key: "workbench",
        ref: workbench,
        height: "auto"
      }, {
        tree: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tree.value, (node) => {
            return (_openBlock(), _createBlock(IfcTreeNode, {
              key: node.expressId,
              node: node,
              depth: 0,
              "expanded-ids": expandedIds,
              "selected-id": selectedNode.value?.expressId ?? -1,
              "existing-records": existingRecords.value,
              onToggle: toggleExpand,
              onSelect: selectNode,
              onOpenExisting: openExistingRecord
            }, null, 8 /* PROPS */, ["node", "expanded-ids", "selected-id", "existing-records"]))
          }), 128 /* KEYED_FRAGMENT */))
        ]),
        "viewport-extras": _withCtx(() => [
          _createVNode(IfcClassVisibilityBar, {
            classes: allClasses.value,
            hidden: hiddenClasses,
            onToggle: toggleClassVisibility
          }, null, 8 /* PROPS */, ["classes", "hidden"])
        ]),
        "viewport-overlay": _withCtx(() => [
          (is3dLoading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_21, [...(_cache[7] || (_cache[7] = [
                _createElementVNode("i", { class: "fa fa-spinner fa-spin" }, null, -1 /* CACHED */)
              ]))]))
            : _createCommentVNode("v-if", true),
          (hoveredNode.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                style: _normalizeStyle({ position: 'absolute', left: (hoverPos.value.x + 12) + 'px', top: (hoverPos.value.y + 12) + 'px', background: 'rgba(0,0,0,0.85)', color: 'white', padding: '6px 10px', borderRadius: '4px', fontSize: '11px', pointerEvents: 'none', zIndex: 20, maxWidth: '260px' })
              }, [
                _createElementVNode("div", _hoisted_22, _toDisplayString(hoveredNode.value.name || 'Unnamed'), 1 /* TEXT */),
                _createElementVNode("div", _hoisted_23, _toDisplayString(hoveredNode.value.ifcClass), 1 /* TEXT */)
              ], 4 /* STYLE */))
            : _createCommentVNode("v-if", true)
        ]),
        properties: _withCtx(() => [
          _createVNode(IfcPropertiesPanel, {
            node: selectedNode.value,
            "show-header-extras": !!selectedNode.value
          }, {
            "header-extras": _withCtx(() => [
              _createCommentVNode(" bSDD enrichment button — importer-only extra. "),
              _createElementVNode("button", {
                class: "btn btn-default btn-xs ifc-props__bsdd-btn",
                title: "Look up this class in the buildingSMART Data Dictionary",
                onClick: openBsddModal
              }, [...(_cache[8] || (_cache[8] = [
                _createElementVNode("i", { class: "fa fa-book" }, null, -1 /* CACHED */),
                _createTextVNode(" bSDD info ", -1 /* CACHED */)
              ]))])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["node", "show-header-extras"])
        ]),
        _: 1 /* STABLE */
      }, 512 /* NEED_PATCH */)
    ], 544 /* NEED_HYDRATION, NEED_PATCH */), [
      [_vShow, hasData.value]
    ])
  ]))
}
}

})